import axios, { AxiosError, AxiosResponse } from 'axios';
import { apiClient, endpoints } from './endpoints';
import { DataItem } from './activities';
import { User } from './users';
import { Machine } from './materials/machines';
import { Tool } from './materials/tools';
import { RentalType } from './materials/rentalTypes';
import { WorkTravelZone } from './sites/workTravelZones';
import { ActivityType } from './dailyReports/activityTypes';

interface UserData {
    _id: string;
    lastName: string;
    firstName: string;
    jobId: {
        _id: string;
        designation: string;
        order: number;
    };
}

interface ReportData {
    _id: string;
    userId: UserData;
    date: string;
    reportState: number;
    __v: number;
    fuel?: number;
    km?: number;
    gnr?: number;
}

export interface Site {
    _id: string;
    codeProC: string;
    name: string;
    stateProC: number;
    conducId?: User;
    assistantConducId?: User;
    address?: string;
    longitude?: number;
    latitude?: number;
    crudStatus: number;
    entityId: string;
    isVisibleInApp?: boolean;
    startDate?: string;
    endDate?: string;
    usageCount?: {
        total: number;
        activities: number;
        dailyTransfers: number;
        dailyTransferRides: number;
        dailyTrucksPlanned: number;
        dailyTrucksRides: number;
        gnrRequests: number;
    };
    updatedAt?: string;
    createdAt?: string;
    type: number;
    workTravelZoneId: WorkTravelZone;
}


export interface ActivityData {
    _id: string;
    activityTypeId: ActivityType;
    reportId: ReportData;
    siteId: string;
    activityState: number;
    machinesState: number;
    rentalsState: number;
    toolsState: number;
    __v: number;
    startTime: string;
    subContractorsState: number;
    tempWorkersState: number;
    validationTimeState: number;
    validationTime?: string;
    validationTimestamp?: string;
    observationsPlanning: string;
    observationsWorker: string;
    observationsPlanningState: number;
    observationsWorkerState: number;
    dailyMachines: DailyMachine[];
    dailyTools: DailyTool[];
    dailyRentals: DailyRental[];
    dailySubContractors: DailySubContractor[];
    dailyTempWorkers: DailyTempWorker[];
}

export interface ActivityDataWithId extends ActivityData {
    id: number;
    siteIdx: number;
}

export interface SiteActivities {
    site: Site;
    activities: ActivityData[];
}

export interface ActivitiesResponse {
    message: string;
    data: SiteActivities[];
}

export interface DailyMachine {
    _id: string;
    machineId: Machine;
    activityId: string;
    state: number;
    isUsed: boolean;
    price?: number;
    priceType?: number;
    quantity: number;
    unitPrice?: number;
}

interface MachineType {
    designation: string;
    _id: string;
}

export interface DailyTool {
    _id: string;
    toolId: Tool;
    activityId: string;
    state: number;
    isUsed: boolean;
    price?: number;
    priceType?: number;
    quantity: number;
    unitPrice?: number;
}

interface ToolType {
    designation: string;
    _id: string;
}

export interface DailyRental {
    _id: string;
    designation: string;
    activityId: string;
    rentalTypeId: RentalType;
    state: number;
    isUsed: boolean;
    price?: number;
    priceType?: number;
    observations?: string;
    quantity: number;
    unitPrice?: number;
    isExposedToWeather?: boolean;
}

export interface DailySubContractor {
    _id: string;
    activityId: string;
    subContractorTypeId: SubContractorType;
    durationDay: number;
    state: number;
    includedInExpenses?: boolean;
    price?: number;
    priceType?: number;
}

interface SubContractorType {
    _id: string;
    designation: string;
    colorId: string;
    codeProC: string;
}

export interface DailyTempWorker {
    _id: string;
    activityId: string;
    tempWorkerTypeId: TempWorkerType;
    durationDay: number;
    durationHours?: number;
    tempWorkerName?: string;
    state: number;
    includedInExpenses?: boolean;
    price?: number;
    priceType?: number;
    unitPrice?: number;
}

interface TempWorkerType {
    _id: string;
    designation: string;
    codeProC: string;
    colorId: string;
}

export interface UpdatedActivity {
    message: string;
    data: DataItem;
}

export const updateActivity = async (activityId: string, updateData: any): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('activities/activity/' + activityId, updateData);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateActivitiesStateBody {
    activityIds: string[];
    activityState: number;
    currentState: number;
}

export interface UpdateActivitiesStateQueries {
    freezePrices?: boolean;
}

export const updateActivitiesState = async (body: UpdateActivitiesStateBody, queries?: UpdateActivitiesStateQueries): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('activities/state', body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const updateActivityTruckManager = async (activityId: string, isTruckManager: boolean): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('activities/activity/' + activityId + '/isTruckManager', {
            isTruckManager,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface CreateDailyMachineResponse {
    message: string;
    data: DailyMachine;
}

export const createDailyMachine = async (
    activityId: string,
    machineId: string,
    state: number,
    createTransfer?: boolean,
    price?: number | null,
    priceType?: number | null,
    quantity?: number
): Promise<CreateDailyMachineResponse> => {
    try {
        const response: AxiosResponse<CreateDailyMachineResponse> = await apiClient.post('/daily-machines', {
            activityId,
            machineId,
            state,
            createTransfer,
            price,
            priceType,
            quantity
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface CreateDailyToolResponse {
    message: string;
    data: DailyTool;
}

export const createDailyTool = async (
    activityId: string,
    toolId: string,
    state: number,
    createTransfer?: boolean,
    price?: number | null,
    priceType?: number | null,
    quantity?: number
): Promise<CreateDailyToolResponse> => {
    try {
        const response: AxiosResponse<CreateDailyToolResponse> = await apiClient.post('/daily-tools', {
            activityId,
            toolId,
            state,
            createTransfer,
            price,
            priceType,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface CreateDailyRentalResponse {
    message: string;
    data: DailyRental;
}

export interface CreateDailyRentalBody {
    activityId: string;
    rentalTypeId: string;
    designation: string;
    state: number;
    createTransfer?: boolean;
    price?: number;
    priceType?: number;
    observations?: string;
    isUsed?: boolean;
    unitPrice?: number;
    quantity?: number;
    isExposedToWeather?: boolean;
}

export const createDailyRental = async (body: CreateDailyRentalBody): Promise<CreateDailyRentalResponse> => {
    try {
        const response: AxiosResponse<CreateDailyRentalResponse> = await apiClient.post('/daily-rentals', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateDailySubContractorResponse {
    message: string;
    data: DailySubContractor;
}

export interface CreateDailySubContractorBody {
    activityId: string;
    subContractorTypeId?: string;
    price?: number;
    priceType?: number;
    includedInExpenses?: boolean;
}

export const createDailySubContractor = async (body: CreateDailySubContractorBody): Promise<CreateDailySubContractorResponse> => {
    try {
        const response: AxiosResponse<CreateDailySubContractorResponse> = await apiClient.post('/daily-subcontractors', {
            durationDay: 1,
            ...body,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateDailyTempWorkerResponse {
    message: string;
    data: DailyTempWorker;
}

export interface CreateDailyTempWorkerBody {
    activityId: string;
    tempWorkerTypeId: string;
    durationHours?: number;
    tempWorkerName?: string;
    price?: number;
    priceType?: number;
    includedInExpenses?: boolean;
    unitPrice?: number;
}

export const createDailyTempWorker = async (body: CreateDailyTempWorkerBody): Promise<CreateDailyTempWorkerResponse> => {
    try {
        const response: AxiosResponse<CreateDailyTempWorkerResponse> = await apiClient.post('/daily-tempworkers', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DeleteDailyMachineResponse {
    message: string;
    data: DailyMachine;
}

export interface DeleteDailyMachineQueries {
    deleteTransfers?: boolean;
}

export const deleteDailyMachine = async (id: string, queries?: DeleteDailyMachineQueries): Promise<DeleteDailyMachineResponse> => {
    try {
        const response: AxiosResponse<DeleteDailyMachineResponse> = await apiClient.delete(`/daily-machines/${id}`, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DeleteDailyToolResponse {
    message: string;
    data: DailyMachine;
}
export interface DeleteDailyToolQueries {
    deleteTransfers?: boolean;
}

export const deleteDailyTool = async (id: string, queries?: DeleteDailyToolQueries): Promise<DeleteDailyToolResponse> => {
    try {
        const response: AxiosResponse<DeleteDailyToolResponse> = await apiClient.delete(`/daily-tools/${id}`, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DeleteDailyRentalResponse {
    message: string;
    data: DailyRental;
}
export interface DeleteDailyRentalQueries {
    deleteTransfers?: boolean;
}

export const deleteDailyRental = async (id: string, queries?: DeleteDailyRentalQueries): Promise<DeleteDailyRentalResponse> => {
    try {
        const response: AxiosResponse<DeleteDailyRentalResponse> = await apiClient.delete(`/daily-rentals/${id}`, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DeleteDailySubContractorResponse {
    message: string;
    data: DailySubContractor;
}

export const deleteDailySubContractor = async (id: string): Promise<DeleteDailySubContractorResponse> => {
    try {
        const response: AxiosResponse<DeleteDailySubContractorResponse> = await apiClient.delete(`/daily-subcontractors/${id}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DeleteDailyTempWorkerResponse {
    message: string;
    data: DailyTempWorker;
}

export const deleteDailyTempWorker = async (id: string): Promise<DeleteDailyTempWorkerResponse> => {
    try {
        const response: AxiosResponse<DeleteDailyTempWorkerResponse> = await apiClient.delete(`/daily-tempworkers/${id}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface Activity {
    _id: string;
    activityTypeId: string;
    reportId: string;
    activityState: number;
    siteId: string;
    startTime: string;
    validationTime?: string;
    validationTimestamp?: string;
    observationsPlanning?: string;
    observationsWorker?: string;
    needsValidationTime: string;
    planningValidationTime: string;
    isTruckManager: boolean;
    needSubmitValidationTimeStamp?: string;
    planningSubmitValidationTimeStamp?: string;
    stepMode: number;
    isSpecialHidden?: boolean;
    isHighlight?: boolean;
    includedInExpenses?: boolean;
    isLongDistance?: boolean;
    longDistancePrice?: number;
}

export interface CreateActivityBody {
    activityTypeId: string;
    siteId: string;
    userId: string;
    startTime: string;
    state?: number;
    activityState: number;
    reportState: number;
    planningValidationTime?: string;
    needsValidationTime?: string;
    stepMode: number;
    observationsWorker?: string;
    observationsPlanning?: string;
    includedInExpenses?: boolean;
    isTruckManager?: boolean;
    quantity?: number;
}

export interface ActivityResponse {
    message: string;
    data: Activity;
}

export const createActivity = async (activityData: CreateActivityBody): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.post('/activities', activityData);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface Profile {
    _id: string;
    permsLevel: number;
    type: string;
}

export interface UserItem {
    _id: string;
    lastName: string;
    firstName: string;
    profileId: Profile;
    isSpecial?: boolean;
}

interface UsersResponse {
    data: UserItem[];
    message: string;
}

export const getUsersByPermsLevel = async (permsLevel: number): Promise<UsersResponse> => {
    try {
        const response: AxiosResponse<UsersResponse> = await apiClient.get('/users/perms-level/' + permsLevel);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getUsersBetweenPermsLevel = async (minPermsLevel: number, maxPermsLevel: number): Promise<UsersResponse> => {
    try {
        const response: AxiosResponse<UsersResponse> = await apiClient.get('/users/perms-level/' + minPermsLevel + '/and/' + maxPermsLevel);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface ActivitiesTypeResponse {
    message: string;
    data: ActivityType[];
}

export interface GetActivitiesTypesQueries {
    active?: boolean;
}

export const getActivitiesTypes = async (queries?: GetActivitiesTypesQueries): Promise<ActivitiesTypeResponse> => {
    try {
        const response: AxiosResponse<ActivitiesTypeResponse> = await apiClient.get('/activities-types', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface MachineTypesResponse {
    message: string;
    data: MachineType[];
}

export const getMachineTypes = async (): Promise<MachineTypesResponse> => {
    try {
        const response: AxiosResponse<MachineTypesResponse> = await apiClient.get('/machine-types');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface ToolTypesResponse {
    message: string;
    data: ToolType[];
}

export const getToolTypes = async (): Promise<ToolTypesResponse> => {
    try {
        const response: AxiosResponse<ToolTypesResponse> = await apiClient.get('/tool-types');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface RentalTypesResponse {
    message: string;
    data: RentalType[];
}

export const getRentalTypes = async (): Promise<RentalTypesResponse> => {
    try {
        const response: AxiosResponse<RentalTypesResponse> = await apiClient.get('/rental-types');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface SubContractorTypesResponse {
    message: string;
    data: SubContractorType[];
}

export const getSubContractorTypes = async (): Promise<SubContractorTypesResponse> => {
    try {
        const response: AxiosResponse<SubContractorTypesResponse> = await apiClient.get('/subcontractor-types');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface TempWorkerTypesResponse {
    message: string;
    data: TempWorkerType[];
}

export const getTempWorkerTypes = async (): Promise<TempWorkerTypesResponse> => {
    try {
        const response: AxiosResponse<TempWorkerTypesResponse> = await apiClient.get('/tempworker-types');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface MachineResponse {
    message: string;
    data: Machine[];
}

export const getMachinesByMachineTypeId = async (machineTypeId: string): Promise<MachineResponse> => {
    try {
        const response: AxiosResponse<MachineResponse> = await apiClient.get('/machines/type/' + machineTypeId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface ToolResponse {
    message: string;
    data: Tool[];
}

export const getToolsByToolTypeId = async (toolTypeId: string): Promise<ToolResponse> => {
    try {
        const response: AxiosResponse<ToolResponse> = await apiClient.get('/tools/type/' + toolTypeId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface SitesResponse {
    message: string;
    data: Site[];
}

export const getSites = async (): Promise<SitesResponse> => {
    try {
        const response: AxiosResponse<SitesResponse> = await apiClient.get('/sites/active');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DeleteActivityResponse {
    message: string;
    data: string;
}

export const deleteActivity = async (activityId: string): Promise<DeleteActivityResponse> => {
    try {
        const response: AxiosResponse<DeleteActivityResponse> = await apiClient.delete('/activities/activity/' + activityId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

interface CreateReportData {
    _id: string;
    userId: string;
    date: string;
    reportState: number;
    __v: number;
}

interface CreateReportResponse {
    message: string;
    data: CreateReportData;
}

export const createReportIfEmpty = async (userId: string, date: string, state: number): Promise<CreateReportResponse> => {
    try {
        const response: AxiosResponse<CreateReportResponse> = await apiClient.post('/reports', {
            userId,
            date,
            state,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

// PLANNING

export const sendPlanning = async (date: string, body: { withNotification?: boolean }): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.post('/planning/date/' + date, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const sendPlanningForActivity = async (activityId: string, body: { withNotification?: boolean }): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.post('/planning/activity/' + activityId, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importNeeds = async (date: string): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.post('/planning/needs/import/date/' + date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getBackPlanning = async (date: string, body: { withNotification?: boolean }): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch('/planning/date/' + date, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getBackPlanningForActivity = async (activityId: string, body: { withNotification?: boolean }): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('/planning/activity/' + activityId, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
